import { logInfo } from 'components/utils/logger';

export const supportsWebp = () => {
  const canvas = typeof document === 'object' ? document.createElement('canvas') : {};
  canvas.width = canvas.height = 1;
  return canvas.toDataURL ? canvas.toDataURL('image/webp').indexOf('image/webp') === 5 : false;
}

export const resizeUploadedImage = (imageFile, imageResizeWidth, callback) => {
  if (!imageFile) {
    callback();
    return;
  }

  const { type: mimeType } = imageFile;
  // eslint-disable-next-line no-undef
  const fileReader = new FileReader();
  fileReader.onabort = () => logInfo('file reading was aborted');
  fileReader.onerror = () => logInfo('file reading has failed');
  fileReader.onload = () => {
    const imageAsBase64 = fileReader.result;
    // eslint-disable-next-line no-undef
    const image = new Image();
    image.onload = function () {
      if (image.width <= imageResizeWidth) {
        callback(imageAsBase64);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = imageResizeWidth;
      canvas.height = ~~(image.height * (imageResizeWidth / image.width));
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) {
        callback(imageAsBase64);
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const resizedImageAsBase64 = canvas.toDataURL(mimeType);
      callback(resizedImageAsBase64);
    };
    image.src = imageAsBase64;
  };
  fileReader.readAsDataURL(imageFile);
}

export const readUploadedImage = (imageFile, callback) => {
  // eslint-disable-next-line no-undef
  const fileReader = new FileReader();
  fileReader.onabort = () => logInfo('file reading was aborted');
  fileReader.onerror = () => logInfo('file reading has failed');
  fileReader.onload = () => {
    callback(fileReader.result);
  }
  fileReader.readAsDataURL(imageFile);
}


export const scaledImageToDataURL = async (file, resizeWidth, resizeHeight = undefined) => {
  // ensure the file is an image
  if (!file.type.match(/image.*/)) return null;

  // eslint-disable-next-line no-undef
  const image = new Image();
  image.src = URL.createObjectURL(file);

  await new Promise((res) => image.onload = res);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", {alpha: true});

  canvas.width = resizeWidth;
  canvas.height = resizeHeight || ~~(image.height * (resizeWidth / image.width));

  let finalWidth, finalHeight;

  if (image.height <= image.width) {
    const scaleProportions = canvas.height / image.height;
    finalWidth = scaleProportions * image.width;
    finalHeight = canvas.height;
    context.drawImage(image, (canvas.width - finalWidth)/2, 0, finalWidth, finalHeight);
  }
  else {
    const scaleProportions = canvas.width / image.width;
    finalWidth = canvas.width;
    finalHeight = scaleProportions * image.height;
    context.drawImage(image, 0, (canvas.height - finalHeight)/2, finalWidth, finalHeight);
  }

  // Convert canvas to DataURL
  const dataURL = canvas.toDataURL(file.type);

  return { dataURL, width: finalWidth, height: finalHeight };
}

export const fileToDataURL = async (file) => {
  // Ensure the file is an image
  if (!file.type.match(/image.*/)) return null;

  // eslint-disable-next-line no-undef
  const reader = new FileReader();

  // Convert the file to a Data URL
  const dataURL = await new Promise((resolve) => {
    reader.onload = (event) => resolve(event.target.result);
    reader.readAsDataURL(file);
  });

  // eslint-disable-next-line no-undef
  const image = new Image();
  image.src = dataURL;

  // Wait for the image to load and get its width and height
  await new Promise((resolve) => (image.onload = resolve));

  // Return both the Data URL and the image's width and height
  return {
    dataURL,
    width: image.width,
    height: image.height
  };
};
